// apps/web/src/features/clients/components/RequisitesSetCard.tsx
//
// One requisites set under a legal entity. Read, select and copy are the
// primary actions here — managers paste this text into invoices — so the copy
// control sits with the text and always yields the ORIGINAL `details`, line
// breaks included (AC-6).
//
// INVARIANT (AC-7/AC-10): archived sets stay readable and copyable but never
// editable, and the LAST active set of an entity cannot be archived — the
// control is disabled with the reason spelled out, matching the backend's
// `ClientLastActiveRequisitesError` guard instead of discovering it via a 409.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LegalEntityRequisites } from '@magimp/shared';
import {
  useArchiveLegalEntityRequisites,
  useRestoreLegalEntityRequisites,
} from '@magimp/shared-hooks';
import { Button, ConfirmModal, StatusBadge } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { useTouchActionStyle } from '../useMobileFieldStyle.js';
import { CopyTextButton } from './CopyTextButton.js';
import { ExpandableText } from './ExpandableText.js';

export interface RequisitesSetCardProps {
  clientId: string;
  legalEntityId: string;
  entityName: string;
  set: LegalEntityRequisites;
  ordinal: number;
  /** False for read-only actors, an archived parent, or an archived entity. */
  canManage: boolean;
  /** True while an ANCESTOR lifecycle write (client or entity) is in flight. */
  busy?: boolean;
  /** True when this is the only active set left on the entity. */
  isLastActive: boolean;
  onEdit: (set: LegalEntityRequisites) => void;
}

export function RequisitesSetCard({
  clientId,
  legalEntityId,
  entityName,
  set,
  ordinal,
  canManage,
  busy = false,
  isLastActive,
  onEdit,
}: RequisitesSetCardProps) {
  const { t } = useTranslation(['clients', 'common']);
  const show = useToastStore((s) => s.show);
  const actionStyle = useTouchActionStyle();
  const archive = useArchiveLegalEntityRequisites(clientId, legalEntityId, set.id);
  const restore = useRestoreLegalEntityRequisites(clientId, legalEntityId, set.id);
  const [confirm, setConfirm] = useState<null | 'archive'>(null);

  const label = t('clients:requisites.setOrdinal', { n: ordinal });
  const isArchived = !!set.archivedAt;
  const pending = archive.isPending || restore.isPending || busy;

  const onArchive = () => {
    archive.mutate(undefined, {
      onSuccess: () => show({ tone: 'success', text: t('clients:toasts.requisitesArchived') }),
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };
  const onRestore = () => {
    restore.mutate(undefined, {
      onSuccess: () => show({ tone: 'success', text: t('clients:toasts.requisitesRestored') }),
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <li
      data-testid="client-requisites-set"
      data-requisites-id={set.id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
        padding: 'var(--s-3) var(--s-4)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: isArchived ? 'var(--bg-2)' : 'var(--bg-1)',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-3)',
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        <span style={{ fontWeight: 500, minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span>
        {isArchived && <StatusBadge status="archived" size="sm" />}
        <span style={{ flex: 1 }} />
        <CopyTextButton
          text={set.details}
          label={t('clients:actions.copyRequisitesOf', { name: label, entity: entityName })}
          testId="client-copy-requisites"
        >
          {t('clients:actions.copyRequisites')}
        </CopyTextButton>
        {canManage && !isArchived && (
          <Button
            size="sm"
            kind="ghost"
            icon="pencil-line"
            style={actionStyle}
            disabled={pending}
            onClick={() => onEdit(set)}
          >
            {t('common:actions.edit')}
          </Button>
        )}
        {canManage && !isArchived && (
          <Button
            size="sm"
            kind="ghost"
            icon="archive"
            style={actionStyle}
            disabled={isLastActive || pending}
            {...(isLastActive ? { 'aria-describedby': `${set.id}-last-active` } : {})}
            onClick={() => setConfirm('archive')}
            data-testid="client-requisites-archive"
          >
            {t('common:actions.archive')}
          </Button>
        )}
        {canManage && isArchived && (
          <Button
            size="sm"
            kind="ghost"
            icon="archive-restore"
            style={actionStyle}
            disabled={pending}
            onClick={onRestore}
          >
            {t('common:actions.restore')}
          </Button>
        )}
      </div>

      {canManage && !isArchived && isLastActive && (
        <p
          id={`${set.id}-last-active`}
          style={{ margin: 0, fontSize: 'var(--fz-11)', color: 'var(--fg-3)' }}
        >
          {t('clients:requisites.lastActiveHint')}
        </p>
      )}

      <ExpandableText
        text={set.details}
        expandLabel={t('clients:actions.expandRequisitesOf', { name: label })}
      />

      {confirm === 'archive' && (
        <ConfirmModal
          danger
          narrowFullWidth
          title={t('clients:confirm.archiveRequisitesTitle')}
          text={t('clients:confirm.archiveRequisitesMessage', { name: label, entity: entityName })}
          confirmLabel={t('common:actions.archive')}
          onConfirm={onArchive}
          onClose={() => setConfirm(null)}
        />
      )}
    </li>
  );
}
